import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  FormControlLabel,
  Switch,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { AccordionWrapper } from '../../utility/AccordianWrapper'
import { Single } from 'infrastructure/api'
import { useUserResource } from '../../../api/user.hooks'
import ArchiveClassButton from '../shared/ArchiveClassButton'
import { PlainLink } from 'app/components/RouteLink'
import ReinstateResourceButton from '../shared/ReinstateResourceButton'
import ResyncDefaultChannelButton from '../shared/ResyncDefaultChannelButton'
import { ResetCustomLesson } from './ResetCustomLesson'
import InitiateImpersonationButton from '../shared/InitiateImpersonationButton'
import EndImpersonationButton from '../shared/EndImpersonationButton'
import { useNavigateTo } from 'infrastructure/routing'

export function UserShow({ id }: { id: number | string }) {
  const { userData: data } = useUserResource(id)
  const archiveButtonLabel = 'Archive All Classes for User'

  const [activeImpersonationData, setActiveImpersonationData] = useState<
    string | null
  >(null)
  useEffect(() => {
    const savedData = sessionStorage.getItem('impersonationData')
    if (savedData) {
      setActiveImpersonationData(savedData)
    }
  }, [])

  const handleImpersonationSuccess = (encryptedData: string) => {
    setActiveImpersonationData(encryptedData)
  }
  const handleStopImpersonationSuccess = () => {
    setActiveImpersonationData(null)
  }

  if (!data) {
    return <div>Loading...</div>
  }

  if (activeImpersonationData) {
    return (
      <div className="content-color">
        <h2>User Impersonation Active</h2>
        <p>
          You are currently impersonating another user with ID {data.id}. Click
          on Stop Impersonation button to return to normal TKAT functionality.
        </p>
        <EndImpersonationButton
          encryptedData={activeImpersonationData}
          onStopImpersonationSuccess={handleStopImpersonationSuccess}
        />
      </div>
    )
  }

  return (
    <div className="content-color">
      <p>
        User name:<b> {data.name} </b>
      </p>
      <p>
        User ID: <b> {data.id} </b>
      </p>
      <p>
        User email:<b> {data.email} </b>
      </p>
      <p>
        User roles: <b> {data.roles?.join(', ')} </b>
      </p>
      <p>
        Created At: <b> {data.created_at} </b>
      </p>
      {data.deleted_at && (
        <p>
          Deleted At: <b> {data.deleted_at} </b>
        </p>
      )}
      <p>
        Last Login:{' '}
        <b> {data.last_login ? data.last_login : 'User has not logged in'}</b>
      </p>
      <p>
        Last Active:{' '}
        <b> {data.last_active ? data.last_active : 'User is not active'}</b>
      </p>
      <p>
        Last Accessed Curriculum:{' '}
        <b>
          {' '}
          {data.last_accessed_curriculum
            ? data.last_accessed_curriculum
            : 'User has not interacted with any curriculum'}
        </b>
      </p>
      {data.school && (
        <p>
          <span>User school: </span>
          <span>
            <PlainLink to={`/schools/${data.school.id}/show`} className="link">
              {data.school.name}
            </PlainLink>
          </span>
        </p>
      )}
      <hr />
      <p>
        <span className="section-buttons">
          <Button
            variant="outlined"
            component={PlainLink}
            to={`/users/${data.id}/edit`}
          >
            Edit User
          </Button>
        </span>
        <InitiateImpersonationButton
          targetUserID={data.id}
          onImpersonationSuccess={handleImpersonationSuccess}
        />
        {data.deleted_at && (
          <ReinstateResourceButton
            name={data.name}
            resource="User"
            userID={data.id}
          />
        )}
      </p>
      <hr />
      <ArchiveClassButton
        id={data.id}
        level={'user'}
        archive={true}
        label={archiveButtonLabel}
      />
      <hr />
      <AccordionWrapper title="Classes:">
        <Classes classes={data.classes} />
      </AccordionWrapper>
    </div>
  )
}

function Classes({ classes = [] }: { classes: Array<Single.UserClass> }) {
  const [isGriffinVisible, setIsGriffinVisible] = useState(false)
  const navigate = useNavigateTo()

  const handleGriffinVisibleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsGriffinVisible(event.target.checked)
  }

  function showArchiveButton(role: string): boolean {
    if (role === 'student') {
      return false
    }
    return true
  }
  function getClassTitle(userClass: Single.UserClass) {
    return (
      <>
        <span>
          {userClass.class.alias}
          {userClass.class.is_archived && (
            <span style={{ color: 'red' }}> -=Archived=- </span>
          )}
        </span>
      </>
    )
  }

  const filteredClasses = isGriffinVisible
    ? classes.filter((userClass) => !userClass.class.is_archived)
    : classes

  const handleClassClick = (id: number | string) => {
    navigate(`/class?class_id=${id}`)
  }

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <div>
          <strong>Class Alias</strong> is the name of a class set in the SIS
          <br />
          <strong>Class Name</strong> (if set) is the name of a class displayed
          in the Kiddom App
        </div>
        <FormControlLabel
          control={
            <Switch
              checked={isGriffinVisible}
              onChange={handleGriffinVisibleChange}
              color="primary"
            />
          }
          label="Show Griffin Options"
        />
      </Box>
      <List>
        {filteredClasses.map((userClass) => (
          <ListItem key={userClass.class.id} alignItems="flex-start">
            <ListItemText
              primary={
                <>
                  <strong>Class ID:</strong> {userClass.class.id}
                  <br />
                  <strong>Class Alias:</strong> {getClassTitle(userClass)}
                  <br />
                  <strong>Class Name:</strong> {userClass.class.name}
                  <br />
                  <strong>Class Code:</strong>{' '}
                  {userClass.class.class_voucher_code || 'N/A'}
                </>
              }
              secondary={userClass.role}
              onClick={() => handleClassClick(String(userClass.class.id))}
              sx={{ cursor: 'pointer' }}
            />
            {isGriffinVisible ? (
              <ResetCustomLesson
                classId={userClass.class.id}
                className={userClass.class.name || userClass.class.alias}
              />
            ) : (
              <>
                {showArchiveButton(userClass.role) && (
                  <ArchiveClassButton
                    id={userClass.class.id}
                    level={'class'}
                    archive={!userClass.class.is_archived}
                    label={
                      (userClass.class.is_archived ? 'Unarchive' : 'Archive') +
                      ' Class'
                    }
                  />
                )}
                <span>&nbsp;&nbsp;&nbsp;</span>
                <ResyncDefaultChannelButton classID={userClass.class.id} />
                <span>&nbsp;&nbsp;&nbsp;</span>
              </>
            )}
          </ListItem>
        ))}
      </List>
    </>
  )
}

function deletedText(deletedAt?: string) {
  return deletedAt ? `Deleted ${deletedAt}` : ''
}

function Curricula({
  curricula = [],
}: {
  curricula: Single.CopiedCurricula[]
}) {
  const [sorted, setSorted] = useState<Single.CopiedCurricula[]>([])

  useEffect(() => {
    const isDeleted = (c: Single.CopiedCurricula) =>
      !!c.deleted_at || c.copied_curriculum.deleted
    curricula.sort((a: Single.CopiedCurricula, b: Single.CopiedCurricula) => {
      // @ts-ignore
      return isDeleted(a) - isDeleted(b)
    })
    setSorted(curricula)
  }, [curricula])
  return (
    <List>
      {sorted.map((copy) => {
        const concernedState =
          !copy.deleted_at &&
          !copy.copied_curriculum.deleted &&
          copy.original_curriculum.deleted
        return (
          <Box key={copy.id}>
            <Box padding="5px" color={concernedState ? 'red' : 'black'}>
              {`ID: ${copy.id} ${copy.copied_curriculum.title}`}
            </Box>
            <Box>
              {`${copy.copied_curriculum.id}: ${copy.original_curriculum.id}`}
            </Box>
            <Box padding="5px" paddingLeft="10px">
              {copy.deleted_at && (
                <Box padding="5px">{`${deletedText(copy.deleted_at)}`}</Box>
              )}
              {copy.copied_curriculum.deleted && (
                <Box padding="5px">Copied curricula has been deleted</Box>
              )}
              {copy.original_curriculum.deleted && (
                <Box padding="5px">Original curricula has been deleted</Box>
              )}
            </Box>
          </Box>
        )
      })}
    </List>
  )
}
